import { SelectionModel } from "@angular/cdk/collections";
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core"
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, FILTER_MODEL_KEY, INSIGHTS_FIELD, Linqset, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, NodeInsights, NodeProperties, ORGANISATION_MODEL_KEY, getInsights, getWarningText, getWarningsForNode, isModelNode, isRolePeople, isRoleSystem, isSystemInfrastructure, updateTagsOnProperties, WORK_TYPE, WORK_TYPE_NAMES } from "../../common/linqset-model";
import { GraphProcessingService } from "../../model-services/graph-processing.service";
import { GroupNodeTag } from "../../canvas/model/graph-model";
import { getTagGroupsForModel, Tag, TagGroup } from "../../common/organisation";
import { TagsService } from "../../organisation/tags/tags-service";
import { contributionField, InsightsFieldType } from "../../insights/model/insights-field-type";
import { NODE_TYPE_DATA_TYPE, NodeTypeData } from "../node-types/node-type-data";
import { NodeRowData } from "./node-row-data";
import { AppComponent } from "../../app.component";
import { ModelService } from "../../organisation/models/model-service";
import { GraphComponent, INode } from "@yfiles/yfiles";
import { CHANGE_TYPE } from "../../common/linqset-change-model";
import { MatDialog } from "@angular/material/dialog";
import { NodeDialogComponent } from "../../model-components/nodes/node-dialog.component";
import { FrequencyDialogComponent, FrequencyDialogData, FrequencyDialogResult } from "../../model-components/tempo-dialog/frequency-dialog.component";
import { DownloadService } from "../../model-services/download.service";
import { NodeTypesComponent } from "../node-types/node-types.component";
import { UserService } from "../../user/user.service";
import { User } from "../../common/user";
import { CHART_TYPE } from "../model-charts/model-charts.component";

const HIDDEN_COLUMNS_KEY = "linq.nodeTable.hiddenColumns"
const FIXED_COLUMNS = ['select', 'icon', 'name']     // always shown, not offered in the column picker

interface EdgeRow {
    edge: LinqsetEdge
    from: string,
    fromType: NODE_TYPE,
    toType: NODE_TYPE,
    to: string
    description: string
}

// a panel narrower than this (as beside a model) gets compact controls
const NARROW_PANEL = 620

@Component({
    selector: 'app-node-table',
    templateUrl: './node-table.html',
    styleUrls: ['./node-table.scss'],
    standalone: false
})
export class NodeTableComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() linqset: Linqset
    @Input() comparison: Linqset

    @Input() graphComponent: GraphComponent
    @Input() organisationLinqset:Linqset

    @Input() multiSelect:boolean
    @Input() isGrouped:boolean
    @Input() selectedTagGroup:TagGroup

    @Input() trigger:any            // trigger reload

    loading:boolean = false       // whether data is loading
    typeLoading = false           // whether the chosen type's nodes are still being fetched
    display:string = "insights"        // showing table or edit or charts
    canEditNodes:boolean = false    // whether allowed to edit nodes

    selection = new SelectionModel<NodeRowData>(true, []);
    groupNodeData:GroupNodeTag[]
    allSelected:boolean

    tags:TagGroup[]

    selectedTypeData:NodeTypeData

    expandedTypes:boolean=true

    insightColumns: InsightsFieldType[] = []
    
    availableColumns:string[]       // every column that applies to the selected node type and view
    displayedColumns:string[]       // the available columns the user hasn't hidden
    hiddenColumns:{[view:string]:string[]} = this.loadHiddenColumns()
    headings:any

    totals:any

    NODE_TYPE = NODE_TYPE
    NODE_SUPERTYPE = NODE_SUPERTYPE
    CHART_TYPE=CHART_TYPE

    dataSource:MatTableDataSource<NodeRowData> = new MatTableDataSource<NodeRowData>()
    // the table (and its sort headers) only exists once a node type is chosen, so connect the sort when it appears
    sort: MatSort
    @ViewChild(MatSort) set sortHeaders(sort:MatSort) {
        this.sort = sort
        if (sort) this.dataSource.sort = sort
    }
    
    edgeDisplayedColumns = ['fromi', 'from', 'type', 'toi', 'to', 'actions'];
    edgeDataSource = new MatTableDataSource<EdgeRow>();

    @Output() selectNodeEvent = new EventEmitter<string[]>();
    @Output() selectEdgeEvent = new EventEmitter<LinqsetEdge>();

    @ViewChild(NodeTypesComponent) nodeTypes:NodeTypesComponent

    // the table fills the room left below it on screen, however tall the controls above it are
    tableHeight:number|undefined
    narrow = false                     // in a narrow panel, such as beside a model: compact controls
    private tableElement:HTMLElement|undefined
    private fitTimer:ReturnType<typeof setTimeout>|undefined
    private resizeObserver:ResizeObserver|undefined
    // panels seen to have a height of their own (see fitTable)
    private readonly fixedPanels = new WeakSet<HTMLElement>()
    private readonly onWindowResize = () => this.scheduleFit()

    @ViewChild('tableContainer') set tableContainer(ref:ElementRef<HTMLElement>|undefined) {
        this.tableElement = ref?.nativeElement
        this.scheduleFit()
    }

    constructor(private graphProcessingService:GraphProcessingService, private modelService:ModelService, private downloadService:DownloadService, private userService:UserService,
        private tagsService:TagsService, private dialog:MatDialog, private changeDetectorRef: ChangeDetectorRef, private host:ElementRef<HTMLElement>) {
    }

    ngOnDestroy(): void {
        clearTimeout(this.fitTimer)
        this.resizeObserver?.disconnect()
        window.removeEventListener('resize', this.onWindowResize)
    }

    // folds the node types away, or back, and gives the table the room
    toggleTypes() {
        this.expandedTypes = !this.expandedTypes
        this.scheduleFit()
    }

    // measured once whatever moved the table has been laid out (a timer rather than an animation frame, which a
    // hidden tab doesn't run)
    private scheduleFit() {
        clearTimeout(this.fitTimer)
        this.fitTimer = setTimeout(() => this.fitTable(), 30)
    }

    // the table's height: from where it starts to the bottom of the window, or of the panel around it that ends first
    // (a side panel can end above the window's foot, and would cut off the scroll bar across the table's foot), as if
    // every panel holding it were scrolled to the top, so it is the same wherever the page is scrolled to; never fewer
    // than a useful number of rows
    private fitTable() {
        const el = this.tableElement
        if (!el || !el.isConnected) return
        const ancestors:HTMLElement[] = []
        for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) ancestors.push(p)
        // the scrolling of everything outside each panel, so its edges are where they'd be with all of it at the top
        const outside = ancestors.map((_, i) => window.scrollY + ancestors.slice(i + 1).reduce((t, p) => t + p.scrollTop, 0))
        const top = el.getBoundingClientRect().top + window.scrollY + ancestors.reduce((t, p) => t + p.scrollTop, 0)
        // a panel that grows with what's in it (a tab's body) ends where the table does, so says nothing of the room
        // there is: only those whose height is their own, and that cut off what doesn't fit, are taken. Once seen to
        // have its own height a panel is remembered as such: a table fitted to it fills it exactly, which would
        // otherwise look like growing with its content, and the table would flick between the two heights
        const growsWithContent = (p:HTMLElement) => {
            if (this.fixedPanels.has(p)) return false
            const contentBottom = Math.max(...Array.from(p.children).map(c => c.getBoundingClientRect().bottom))
            const grows = p.scrollHeight <= p.clientHeight + 1
                && Math.abs(contentBottom + parseFloat(getComputedStyle(p).paddingBottom || '0') - p.getBoundingClientRect().bottom) <= 1
            if (!grows) this.fixedPanels.add(p)
            return grows
        }
        const bottom = Math.min(window.innerHeight + window.scrollY, ...ancestors
            .map((p, i) => ({ p, i, style: getComputedStyle(p) }))
            .filter(({ p, style }) => style.overflowY !== 'visible' && !growsWithContent(p))
            .map(({ p, i }) => p.getBoundingClientRect().bottom + outside[i]))
        const height = Math.max(320, Math.floor(bottom - top - 16))
        if (height !== this.tableHeight) {
            this.tableHeight = height
            this.changeDetectorRef.detectChanges()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.nodeTypes) {
            if ((changes["linqset"] || changes["selectedTagGroup"] || changes["isGrouped"]) && this.linqset ) {
                this.tags = getTagGroupsForModel(this.linqset.organisation, this.linqset.modelKey)
                this.nodeTypes.refreshData(this.linqset, this.comparison, true, this.isGrouped, this.selectedTagGroup)
            }else if (this.linqset && this.graphComponent && changes["trigger"]) {
                this.nodeTypes.refreshData(this.linqset, this.comparison, false, this.isGrouped, this.selectedTagGroup)
            }
        }
    }

    ngAfterViewInit() {
        // the controls above the table change height as types load, fold away or wrap; the window as it is resized
        this.resizeObserver = new ResizeObserver(() => {
            const narrow = this.host.nativeElement.clientWidth < NARROW_PANEL
            if (narrow !== this.narrow) {
                this.narrow = narrow
                this.changeDetectorRef.detectChanges()
            }
            this.scheduleFit()
        })
        this.resizeObserver.observe(this.host.nativeElement)
        window.addEventListener('resize', this.onWindowResize)
        const _this = this
        this.dataSource.filterPredicate = function (record,filter) {
            const text = [record.name, record.description, record.model?.name, record.role, record.system].filter(t => t).join(' ').toLowerCase()
            const result = text.indexOf(filter) > -1
            if (!result) _this.selection.deselect(record)
            return result
        }

        // figures live in insightsValues, tags sort by their order in the group, and text sorts ignoring case
        this.dataSource.sortingDataAccessor = (row:NodeRowData, column:string) => {
            if (column === 'tag') return row.tags?.length > 0 ? row.tags[0].tagIndex : -1
            if (column in row.insightsValues) return row.insightsValues[column] ?? 0
            const value:any = row[column]
            if (value && typeof value === 'object') return (value.name || '').toLowerCase()
            if (typeof value === 'string') return value.toLowerCase()
            return value ?? ''
        }

        if (this.nodeTypes) {
            this.tags = getTagGroupsForModel(this.linqset.organisation, this.linqset.modelKey)
            this.nodeTypes.refreshData(this.linqset, this.comparison, true, this.isGrouped, this.selectedTagGroup)
        }
    }

    isHelpOn() {
        return AppComponent.help
    }

    exportTable() {
        if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.MODELS)
            this.downloadService.createCSVDownloadForModels(this.linqset.organisation, this.selectedTypeData.models, this.linqset.workspaceKey, this.linqset.workspaceName + "-models")
        else this.downloadService.createCSVDownloadForNodes(this.linqset.organisation, this.dataSource.filteredData.map(d => d.node), this.linqset.modelKey, this.linqset.workspaceKey, this.linqset.model.name)
    }

    selectNodeKey(nodeKey:string) {
        this.selectNodeEvent.emit([nodeKey])
    }

    selectNode(node:INode) {
        const element = document.getElementById(node.tag.node._key);
        if (element) element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    hasColumn(name:string) {
        return this.displayedColumns && this.displayedColumns.find(c => c === name)?true:false
    }

    isRoleSystem() {
        return this.selectedTypeData && isRoleSystem(this.selectedTypeData.superType)
    }

    setupHeadings() {
        this.headings = {}
        this.headings["select"] = "Select"
        this.headings["icon"] = "Type"
        this.headings["name"] = "Name"
        this.headings['description'] = "Description"
        this.headings["role"] = "Roles"
        this.headings["system"] = "System"
        this.headings["model"] = "Model"
        this.headings["models"] = "Models"
        this.headings["from"] = "From"
        this.headings["asset"] = "Info/Asset"
        this.headings["change"] = "Change"
        this.headings['url'] = "URL"
        this.headings['notes'] = "Notes"
        this.headings['duration'] = "Duration"
        // for roles and people this is their working time (e.g. 8 hours each day), not the calculated capacity column
        this.headings['frequency'] = isRolePeople(this.selectedTypeData.superType)?"Availability":"Frequency"
        this.headings['processingType'] = "Inputs"
        this.headings['workType'] = "Kind of work"
        this.headings['tag'] = "Tag"

        // editable
        //this.headings['actionProcessType'] = "Processing"
        this.headings['tagKeys'] = "Tags"
        
        // editable numbers
        this.headings['percentageModelled'] = "% modelled"
        this.headings['hourlyCost'] = "Hourly cost"
        this.headings['fixedCost'] = "Annual cost"
        this.headings['annualCost'] = "Person cost"
        this.headings['unitCost'] = "Hourly cost"
        this.headings['people'] = "People"
        this.headings['units'] = "Units"
        this.headings['outputValue'] = 'Output value'
        this.headings['outputRevenue'] = 'Output revenue'

        this.headings['lifetime'] = 'Lifetime'

    }

    createFieldType(field:INSIGHTS_FIELD) {
        return new InsightsFieldType(field, this.linqset.modelKey, this.linqset.workspaceKey, this.comparison && this.comparison.workspaceKey)
    }

    isExternalRole(row:NodeRowData) {
        return row.node?.type === NODE_TYPE.EXTERNAL_ORGANISATION
    }

    // the model's tag groups have at least one tag in them
    private hasTagsToChoose() {
        const groups = this.tags || (this.linqset ? getTagGroupsForModel(this.linqset.organisation, this.linqset.modelKey) : [])
        return (groups || []).some(g => (g?.tags?.length || 0) > 0)
    }

    setupEditableDisplayColumns() {
        this.availableColumns = this.multiSelect?["select", "icon", "name"]:["icon", "name"]

        switch (this.selectedTypeData.superType) {
            case NODE_SUPERTYPE.ACTION:
                // tags are only chosen here when there are some to choose
                this.availableColumns.push("model", ...(this.hasTagsToChoose() ? ["tagKeys"] : []), "duration", "frequency")
                break
            case NODE_SUPERTYPE.INFORMATION:
                this.availableColumns.push("model", "asset", "outputValue", "outputRevenue")
                break
            case NODE_SUPERTYPE.NOTES:
                this.availableColumns.push("url", "notes")
                break
            case NODE_SUPERTYPE.INFRASTRUCTURE:
                this.availableColumns.push("percentageModelled", "lifetime", "units", "hourlyCost", "fixedCost", "annualCost", "unitCost")
                break
            case NODE_SUPERTYPE.SYSTEM:
                this.availableColumns.push("percentageModelled", "hourlyCost", "fixedCost", "annualCost")
                break
            case NODE_SUPERTYPE.PEOPLE:
                this.availableColumns.push("duration", "frequency", "percentageModelled", "people", "hourlyCost", "annualCost")
                break
            case NODE_SUPERTYPE.ROLE:
                this.availableColumns.push("duration", "frequency", "percentageModelled", "people", "hourlyCost", "annualCost")
                break
            case NODE_SUPERTYPE.OUTCOMES:
                this.availableColumns.push("description")
                break
        }
    }

    getPlaceHolder(column:string) {
        if (column==='percentageModelled') return '100'
        if (column==='people') return '1'
        return '0'
    }

    setDisplayColumns() {
        this.availableColumns = this.multiSelect?["select", "icon", "name"]:["icon", "name"]
        if (this.comparison) this.availableColumns.push('change')
        if ((this.linqset.isOrganisationModel() || this.linqset.isFilterModel()) && (isModelNode(this.selectedTypeData.superType) || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.INPUT 
            || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.OUTPUT)) {
                
            this.availableColumns.push('model')
        }
        if (isRoleSystem(this.selectedTypeData.superType)) {
            if (this.display === 'model') this.availableColumns.push('model')
            else this.availableColumns.push('models')
        }

        if (this.selectedTypeData.superType === NODE_SUPERTYPE.NOTES) {
            this.availableColumns = this.availableColumns.concat(["url", "notes"])
        }else {
            if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.GROUP) {
                this.availableColumns.push('description')
            }
            if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.MODELS) {
                this.availableColumns.push('tag')
            }

            if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.INPUT) {
                this.availableColumns.push('from')
            }

            if (this.selectedTypeData.superType === NODE_SUPERTYPE.ACTION && this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.STANDARD && this.linqset.hasModelNodes) {
                this.availableColumns.push('role')
                this.availableColumns.push('system')
            }
            if (this.selectedTypeData.superType === NODE_SUPERTYPE.INFORMATION) {
                this.availableColumns.push('asset')
            }
            this.availableColumns = this.availableColumns.map(cc => cc)
            if (this.selectedTypeData.superType === NODE_SUPERTYPE.ACTION) {
                this.availableColumns = this.availableColumns.concat(["duration", "frequency"])
            }

            if (isRolePeople(this.selectedTypeData.superType)) {
                this.availableColumns.push("frequency")
            }

            if (this.selectedTypeData.superType === NODE_SUPERTYPE.ACTION && this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.STANDARD) {
                this.availableColumns.push("processingType", "workType")
            }

            if ((this.selectedTypeData.superType === NODE_SUPERTYPE.ACTION || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.GROUP) && (this.selectedTagGroup || this.linqset?.filter?.nodeTags?.length>0)) {
                this.availableColumns.push("tag")
            }

            const includeCarbon = this.organisationLinqset?.insights?.allocatedCarbonByScope?.total > 0
            const includeCapacity = this.organisationLinqset?.insights?.modelledCapacity > 0

            // the work, roles and systems show the value and revenue they contribute (see usesContribution)
            const valueField = (f:INSIGHTS_FIELD) => this.createFieldType(contributionField(f, this.selectedTypeData.superType, this.selectedTypeData.dataType))
            const potentialColumns = [this.createFieldType(INSIGHTS_FIELD.ANNUAL_HOURS), this.createFieldType(INSIGHTS_FIELD.COST), this.createFieldType(INSIGHTS_FIELD.EXCEPTION_WORK), this.createFieldType(INSIGHTS_FIELD.ACTIONS_SUPPORTED),
                    this.createFieldType(INSIGHTS_FIELD.PROCESSED), this.createFieldType(INSIGHTS_FIELD.INFORMATION_SUPPLIED), valueField(INSIGHTS_FIELD.VALUE), valueField(INSIGHTS_FIELD.REVENUE),
                    this.createFieldType(INSIGHTS_FIELD.REVENUE_MARGIN), this.createFieldType(INSIGHTS_FIELD.REVENUE_AT_RISK)
                ]

            if (includeCarbon) potentialColumns.push(this.createFieldType(INSIGHTS_FIELD.CARBON))
            if (includeCapacity) potentialColumns.push(this.createFieldType(INSIGHTS_FIELD.CAPACITY))

            this.insightColumns = []
            let shaded = true

            potentialColumns.forEach(pc => {
                if (pc.doesFieldTypeApplyForTypeData(this.selectedTypeData)) {
                    this.insightColumns.push(pc)
                    this.availableColumns.push(pc.type)
                    if (this.comparison) {
                        this.availableColumns.push(pc.type + "2")
                    }

                    this.headings[pc.type] = pc.getTitle(this.selectedTypeData.dataType, this.selectedTypeData.superType)
                    // the second column holds whichever workspace isn't current state (see NodeRowData)
                    if (this.comparison) this.headings[pc.type + "2"] = this.comparison.workspaceKey === CURRENT_STATE_PROJECT_KEY?this.linqset.workspaceName:this.comparison.workspaceName
                    pc.shaded = shaded
                    shaded = !shaded
                }
            })            
        }
    }

    tagSelectionChange(node:LinqsetNode, tags:any[]) {
        updateTagsOnProperties(this.linqset.organisation, node.properties, tags)
        const change = {properties:{tagKeys:node.properties.tagKeys}}
        this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, change)
    }

    getFieldType(column:string) {
        return (column === 'name' || column === 'description' || column === 'url' || column === 'notes')?'text':'number'
    }

    readonly workTypes = [WORK_TYPE.VALUE_ADDING, WORK_TYPE.NECESSARY, WORK_TYPE.EXCEPTION, WORK_TYPE.NOT_VALUE_ADDING, WORK_TYPE.WAITING].map(kind => ({kind, name: WORK_TYPE_NAMES[kind]}))

    // the kind of work an action is, as the user says; nothing leaves it to LINQ's read
    workTypeChanged(node:LinqsetNode, kind:WORK_TYPE|'') {
        if (!node.properties) node.properties = {} as NodeProperties
        node.properties.workType = kind || null
        this.fieldChanged(node, 'workType')
    }

    fieldChanged(node:LinqsetNode, column:string) {
        let change:any = {}

        if (column === 'asset') {
            change.informationAssetKey = node.informationAssetKey
        }else if (column === 'name' || column === 'description' || column === 'url' || column === 'notes') {
            change[column] = node[column]
        }else {
            change.properties = {}
            change.properties[column] = node.properties[column]
        }

        this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, change)
    }

    openFrequencyDialog(row:NodeRowData) {
        const {node} = row
        const data:FrequencyDialogData = {properties:node.properties, superType:node.superType, type:node.type}

        const dialogRef = this.dialog.open(FrequencyDialogComponent, {data, width:'610px', hasBackdrop:true });
        dialogRef.afterClosed().subscribe((result:FrequencyDialogResult|undefined) => {
            if (result)  {
                if (!node.properties) node.properties = {} as NodeProperties
                if (result.durationChange) {
                    node.properties.duration = result.duration
this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, {_key:node._key, properties:{duration:result.duration}})
                }
                if (result.frequencyChange) {
                    node.properties.frequency = result.frequency
                    this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, {_key:node._key, properties:{frequency:result.frequency}})
                }
            }
        })
    }
    
    getHeaderStyle(column:string) {
        const columnName = column.endsWith('2')?column.substring(0, column.length-1):column
        const ic = this.insightColumns.find(ic => ic.type === columnName)

        // a faint tint marks the columns compared across workspaces
        return ic && ic.shaded?"background:rgba(0, 169, 165, 0.05);":""
    }

    getNumberStyle(row:any, column:string) {
        const background = this.getHeaderStyle(column)

        // work that costs more than the revenue it creates
        if (column === INSIGHTS_FIELD.REVENUE_MARGIN && row[column] < 0) return "color:#b42318;" + background
        if (!column.endsWith('2')) return background

        const before = row[column.substring(0, column.length-1)]
        const after = row[column]

        // up in green, down in red, unchanged in grey
        if (after > before) return "color:#15803d;font-weight:600;" + background
        else if (before > after) return "color:#b42318;font-weight:600;" + background
        else return "color:#9aa0b0;" + background
    }

    getHeadingWidth(column:string) {
        return this.comparison && this.insightColumns.find(c => c.type.toString() === column)?2:1
    }

    // the money columns, and their comparison columns, show as currency
    isMoney(column:string) {
        const money:string[] = [INSIGHTS_FIELD.COST, INSIGHTS_FIELD.REVENUE, INSIGHTS_FIELD.REVENUE_CONTRIBUTION, INSIGHTS_FIELD.REVENUE_MARGIN, INSIGHTS_FIELD.REVENUE_AT_RISK, INSIGHTS_FIELD.EXCEPTION_WORK]
        return money.includes(column) || money.some(m => column === m + "2")
    }

    // value is totalled only where the work ends, for outputs and outcomes: elsewhere it carries through each step, so a
    // total would count it again at every one
    addTotals(row:NodeRowData, comparisonInsights?:any) {
        const t = this.selectedTypeData
        const valueTotals = !!t && (t.dataType === NODE_TYPE_DATA_TYPE.OUTPUT || t.superType === NODE_SUPERTYPE.OUTCOMES)
        // a figure a row leaves blank (an external organisation's capacity) isn't added, so a column of blanks has no total
        const add = (key:string) => {
            if (!(key in row.insightsValues)) return
            this.totals[key] = (this.totals[key] || 0) + (row.insightsValues[key] || 0)
        }
        // revenue at risk isn't totalled either: the steps on one route each put the same revenue at risk
        this.insightColumns.filter(c => (c.type !== INSIGHTS_FIELD.VALUE || valueTotals) && c.type !== INSIGHTS_FIELD.REVENUE_AT_RISK).forEach(c => {
            add(c.type)
            if (comparisonInsights) add(c.type + "2")
        })
    }

    setCanEdit() {
        if (this.linqset?.readonly || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.GROUP || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.MODELS || this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.INPUT) {
            this.canEditNodes = false
            if (this.display === 'edit') this.display = 'insights'
            return
        }
        this.canEditNodes = true
    }

    changeDisplay() {
        this.loading = true

        setTimeout(() => {
            this.setupData()
        }, 50);
    }

    refreshData() {
        this.totals = {}
        let data = []
        const modelMap = {}
        const user = this.userService.getUser()

        try {
            if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.MODELS) {
                data = this.selectedTypeData.models.map(m => {
                    const insights = m.insights?.find(i => i.projectKey === this.linqset.workspaceKey)
                    const n = {_key:m._key, name:m.name, type:NODE_TYPE.MODEL} as LinqsetNode
                    const comparisonInsights = this.comparison && m.insights?.find(i => i.projectKey === this.comparison.workspaceKey)
                    const tags = this.linqset.organisation.modelTags?.tags.filter(t => m.tagKeys?.includes(t._key)) || []
                    const row = new NodeRowData(n, m._key, insights, this.selectedTypeData.dataType, this.insightColumns, [], [], comparisonInsights)
                    row.tags = tags

                    this.addTotals(row, comparisonInsights)

                    return row
                })
            }else if (this.selectedTypeData.dataType === NODE_TYPE_DATA_TYPE.GROUP) {
                data = this.selectedTypeData.groups.map(g => {
                    const n = {_key:g._key, name:g.name, description:g.description} as LinqsetNode
                    const row = new NodeRowData(n, this.linqset.modelKey, g.insights, this.selectedTypeData.dataType, this.insightColumns, [], [], undefined)
                    if (g.tag) row.tags.push(g.tag)

                    row.icon = this.tagsService.getIconForTag(g.tag)
                    this.addTotals(row)
                    return row
                })
            }else {
                data = this.selectedTypeData.list.reduce((a, n) => {
                    this.processNode(n, modelMap, a, user)
                    return a
                }, [])
                if (this.comparison && this.selectedTypeData.comparisonList) {
                    this.selectedTypeData.comparisonList.filter(n => !this.selectedTypeData.list.find(n2 => n2._key === n._key)).forEach(n => {
                        const comparisonInsights = getInsights(n, this.comparison)
                        const row = new NodeRowData(n, n.modelKey, getInsights(n, this.linqset), this.selectedTypeData.dataType, this.insightColumns, getWarningsForNode(n, this.linqset, user.preferences?.fieldTypes, !this.linqset.hasModelNodes), [], comparisonInsights)
                        row.change = "Removed"    
                        this.setRolesSystemAndModel(this.comparison, row, n, modelMap)
                        data.push(row)    
                        this.addTotals(row, comparisonInsights) 
                    })
                }
            }
            this.dataSource.data = data
            if (this.sort) this.dataSource.sort = this.sort
        }catch (error) {
            console.error("Error setting up node table data", error)
        }finally {
            this.loading = false
            this.changeDetectorRef.detectChanges()
        }

    }

    setupData() {
        if (!this.selectedTypeData) return

        this.setCanEdit()
        this.selection.clear()
        this.setupHeadings()

        if (this.display === 'edit') this.setupEditableDisplayColumns()
        else this.setDisplayColumns()
        this.updateDisplayedColumns()

        this.refreshData()
        if (this.graphComponent) {
            this.graphComponent.selection.filter(i => (i instanceof INode) && i.tag && i.tag.node).forEach(i => {
                const node = this.dataSource.filteredData.find(d => d._key === i.tag.node._key)
                if (node) this.selection.select(node)
            })
        }
    }

    processNode(n:LinqsetNode, modelMap:any, a:NodeRowData[], user:User) {
        const tagKey = n.superType === NODE_SUPERTYPE.ACTION && this.selectedTagGroup && n.properties.tagKeys && n.properties.tagKeys[this.selectedTagGroup._key]
        const tag = tagKey && this.selectedTagGroup.tags.find(t => t._key === tagKey)

        if (this.display === "model") {
            n.insights.filter(insights => insights.projectKey === this.linqset.workspaceKey && insights.modelKey !== FILTER_MODEL_KEY && insights.modelKey !== ORGANISATION_MODEL_KEY).forEach(insights => {
                const comparisonInsights = this.comparison && n.insights.find(i => i.projectKey === this.comparison.workspaceKey && i.modelKey === insights.modelKey)

                a.push(this.createTableRow(n, insights.modelKey, tag, insights, comparisonInsights, modelMap, user))
            })
        }else {
            const insights = getInsights(n, this.linqset)
            const cn = this.comparison && this.selectedTypeData.comparisonList.find(c => c._key === n._key)
            const ci:NodeInsights = this.comparison && getInsights(cn||n, this.comparison)
            a.push(this.createTableRow(n, n.modelKey, tag, insights, ci, modelMap, user, cn))
        }

    }

    createTableRow(node:LinqsetNode, modelKey:string, tag:Tag, insights:NodeInsights, comparisonInsights:NodeInsights, modelMap:any, user:User, comparisonNode?:LinqsetNode) {
        // fix up missing people count
        if (isRolePeople(this.selectedTypeData.superType) && node.properties.people === 0) node.properties.people = 1

        const row = new NodeRowData(node, modelKey, insights, this.selectedTypeData.dataType, this.insightColumns, getWarningsForNode(node, this.linqset, user.preferences?.fieldTypes, !this.linqset.hasModelNodes), [], comparisonInsights)
        if (tag) row.tags.push(tag)
        this.addTotals(row, comparisonInsights)
        
        if (this.comparison) {
            if (comparisonNode) {
                if (node.properties.projectKey === CURRENT_STATE_PROJECT_KEY) row.change = ""
                else row.change = "Changed"
            }else row.change = "New"
        }
        this.setRolesSystemAndModel(this.linqset, row, node, modelMap)
        return row
    }

    setRolesSystemAndModel(set:Linqset, row:NodeRowData, n:LinqsetNode, modelMap:any) {
        const {organisation} = set
        if (this.availableColumns.find(c => c === 'role')) {
            const roles = []
            const edges = set.edges.filter(e => e.targetKey === n._key && (e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE))
            edges.forEach(e => {
                const node = set.getNode(e.sourceKey)
                if (node) {
                    if (node.superType === NODE_SUPERTYPE.ROLE) {
                        roles.push(node.name)
                        const systemEdge = set.edges.find(e2 => e2.targetVisualisationKey === e.sourceVisualisationKey && e2.type === EDGE_TYPE.USEBY)
                        if (systemEdge) {
                            const systemNode = set.getNode(systemEdge.sourceKey)
                            if (systemNode) row.system = systemNode.name
                        }
                    }else row.system = node.name
                }
            })
            row.role = roles.join(', ')
        }
        if (row.modelKey) {
            if (!modelMap[row.modelKey]) {
                modelMap[row.modelKey] = row.modelKey === ORGANISATION_MODEL_KEY?organisation.model:organisation.models.find(m => m._key === row.modelKey)
            }
            row.model = modelMap[row.modelKey]
        }
        if (this.availableColumns.find(c => c === 'models')) {
            row.modelNames = n.insights.filter(i => i.projectKey===this.linqset.workspaceKey && i.modelKey !== ORGANISATION_MODEL_KEY && i.modelKey !== FILTER_MODEL_KEY).reduce((a, i) => {
                if (i.modelKey !== ORGANISATION_MODEL_KEY) {
                    if (!modelMap[i.modelKey]) {
                        modelMap[i.modelKey] = organisation.models.find(m => m._key === i.modelKey)?.name||""
                    }
                    a.push(modelMap[i.modelKey])
                }
                return a
            }, [])
        }
        if (this.availableColumns.find(c => c === 'from')) {
            if (n.superType === NODE_SUPERTYPE.ACTION) row.from = "Capture"
            else {
                if (!modelMap[n.modelKey]) {
                    modelMap[n.modelKey] = organisation.models.find(m => m._key === n.modelKey)
                }
                row.from = modelMap[n.modelKey].name
            }
        }
        if (this.availableColumns.find(c => c === 'asset')) {
            if (n.superType === NODE_SUPERTYPE.ACTION) {
                row.asset = set.edges.filter(e => e.targetKey === n._key && e.type === EDGE_TYPE.SUPPORT).map(e => {
                    const i = set.getNode(e.sourceKey)
                    if (i) {
                        if (n.informationAssetKey && n.informationAssetKey.length > 0) {
                            const a = organisation.informationAssets.find(ia => ia._key === n.informationAssetKey)
                            if (a) return a.name
                        }else return i.name
                    }
                    return ""
                }).join(", ")
            }else if (n.informationAssetKey && n.informationAssetKey.length > 0) {
                const a = organisation.informationAssets.find(ia => ia._key === n.informationAssetKey)
                if (a) row.asset = a.name
            }
        }
    }

    gotoModel(row:NodeRowData) {
        this.modelService.editModel(this.linqset.organisation, row.model, this.linqset.workspaceKey, row._key)
    }

    getWarningDescription(row:NodeRowData) {
        return row.warnings.map(w => getWarningText(w)).join(', ')
    }

    onTypeLoading(loading:boolean) {
        this.typeLoading = loading
        // shown at once, as the fetch that follows keeps the page busy
        this.changeDetectorRef.detectChanges()
    }

    selectType(type:NodeTypeData) {
        this.selectedTypeData = type
        this.loading = true
        if (this.display === 'model' && !this.isRoleSystem()) this.display = "insights"
        if (this.display === 'capacity-charts' && !this.hasCapacity()) this.display = "insights"
        if (this.display === 'heatmap-charts' && !this.hasModelSpread()) this.display = "insights"
        if (this.display === 'compare-charts' && !this.canCompare()) this.display = "insights"

        this.changeDetectorRef.detectChanges()

        setTimeout(() => {
            this.setupData()
        }, 50);
    }

    getColourStyleForTag(tag:Tag) {
        return this.tagsService.getColourStyleForTagIndex(tag.tagIndex)
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    selectEdge(edge:LinqsetEdge) {
        this.selectEdgeEvent.emit(edge)
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.allSelected) {
            this.selection.clear();
            this.selection.select(...this.dataSource.filteredData);
        }else {
            this.selection.clear();
        }
        
        this.sendSelectEvent()
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: NodeRowData): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.name}`;
    }
   
    sendSelectEvent() {
        this.selectNodeEvent.emit(this.selection.selected.map(d => d._key))
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    isChartDisplay() {
        return this.display.endsWith('-charts')
    }

    chartType() {
        switch (this.display) {
            case 'bar-charts': return CHART_TYPE.BAR
            case 'capacity-charts': return CHART_TYPE.CAPACITY
            case 'pareto-charts': return CHART_TYPE.PARETO
            case 'heatmap-charts': return CHART_TYPE.HEATMAP
            case 'compare-charts': return CHART_TYPE.COMPARE
            default: return CHART_TYPE.BUBBLE
        }
    }

    // roles and people have a capacity to measure their work against (a list of them, not a model or group total)
    hasCapacity() {
        const t = this.selectedTypeData
        return !!t && isRolePeople(t.superType) && t.dataType !== NODE_TYPE_DATA_TYPE.GROUP && t.dataType !== NODE_TYPE_DATA_TYPE.MODELS
    }

    // roles, people, systems and infrastructure work across models, so can be shown against them
    hasModelSpread() {
        const t = this.selectedTypeData
        return !!t && (isRolePeople(t.superType) || isSystemInfrastructure(t.superType)) && t.dataType !== NODE_TYPE_DATA_TYPE.GROUP && t.dataType !== NODE_TYPE_DATA_TYPE.MODELS
    }

    // before and after needs a workspace to compare with; groups have no figures for it
    canCompare() {
        return !!this.comparison && !!this.selectedTypeData && this.selectedTypeData.dataType !== NODE_TYPE_DATA_TYPE.GROUP
    }

    isTableDisplay() {
        return this.display === 'insights' || this.display === 'model' || this.display === 'edit'
    }

    clearFilter(input:HTMLInputElement) {
        input.value = ''
        this.dataSource.filter = ''
    }

    // column picker. Columns are chosen separately for each node type and view (insights or edit), since each has its own columns

    get optionalColumns() {
        return (this.availableColumns||[]).filter(c => FIXED_COLUMNS.indexOf(c) < 0 && !this.isComparisonColumn(c))
    }

    isColumnVisible(column:string) {
        const hidden = this.hiddenColumns[this.getColumnsView()] || []
        return hidden.indexOf(this.isComparisonColumn(column)?column.substring(0, column.length-1):column) < 0
    }

    hiddenColumnCount() {
        return this.optionalColumns.filter(c => !this.isColumnVisible(c)).length
    }

    toggleColumn(column:string) {
        const view = this.getColumnsView()
        const hidden = this.hiddenColumns[view] || []
        this.hiddenColumns[view] = this.isColumnVisible(column)?[...hidden, column]:hidden.filter(c => c !== column)
        this.saveHiddenColumns()
        this.updateDisplayedColumns()
    }

    showAllColumns() {
        delete this.hiddenColumns[this.getColumnsView()]
        this.saveHiddenColumns()
        this.updateDisplayedColumns()
    }

    private updateDisplayedColumns() {
        this.displayedColumns = (this.availableColumns||[]).filter(c => this.isColumnVisible(c))
    }

    // a comparison workspace's value sits beside its column and is shown or hidden with it
    private isComparisonColumn(column:string) {
        return column.endsWith('2') && this.insightColumns.some(ic => ic.type + '2' === column)
    }

    private getColumnsView() {
        return `${this.display === 'edit'?'edit':'insights'}:${this.selectedTypeData?.superType}:${this.selectedTypeData?.dataType}`
    }

    // column choice is a per-browser convenience, so it lives in local storage rather than the user's saved preferences
    private loadHiddenColumns():{[view:string]:string[]} {
        try {
            const stored = localStorage.getItem(HIDDEN_COLUMNS_KEY)
            if (stored) return JSON.parse(stored)
        }catch(err) {
            console.error("Unable to load node table columns", err)
        }
        return {}
    }

    private saveHiddenColumns() {
        try {
            localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(this.hiddenColumns))
        }catch(err) {
            console.error("Unable to save node table columns", err)
        }
    }

    updateNode(node:NodeRowData) {
        const select = this.graphComponent?.graph && this.graphComponent.graph.nodes.find(n => n.tag?.node?._key === node.node._key)
        if (select && this.graphComponent) NodeDialogComponent.open(this.dialog, this.linqset.organisation, this.graphComponent, this.linqset, this.organisationLinqset, select)
    }
}
